import React, {useEffect, useRef} from 'react';
import PropTypes from 'prop-types';
import {HubConnectionState} from '@microsoft/signalr';
import {connect} from 'react-redux';
import {bindActionCreators} from 'redux';
import useSignalR from './hooks/useSignalR';
import {addNotification} from '../redux/actions/NotificationActions';
import {addAlert} from '../redux/actions/AlertsActions';
import {roles} from '../utils/auth';
import {EventEmitter, EVENTS} from '../api/events';
import {addNotificationGroup, removeNotificationGroup, setNotificationGroups} from '../redux/actions/NotificationGroupsActions';

/** Defines the group that is created on the notification server by {@link AlertsHub}. */
export const NotificationGroups = {
    /* ## User Groups ## */

    /* Message Group Grace User */
    Grace: 'GRACE',

    /* Messages group for SMS User */
    Sms: 'SMS',

    /* Message group from system Administrators*/
    Admin: 'Admin',

    /* Message group for Business account mangers */
    BusinessAccountManagers(companyId) {
        // noinspection JSConstructorReturnsPrimitive
        return `${companyId}-BusinessAccountManager`;
    },

    /* Message group Customers Representative for a specific customer*/
    CustomerRepresentatives(customerId) {
        // noinspection JSConstructorReturnsPrimitive
        return `${customerId}-Representatives`;
    },

    /* Everyone for a company */
    Company(companyId) {
        // noinspection JSConstructorReturnsPrimitive
        return `${companyId}`;
    },

    /* ## Object Groups ## */

    /* Message group for user's listening to all contract changes */
    Contracts: 'Contracts',

    /* Message Group for a use's listening to a specific contract */
    Contract(contractId) {
        // noinspection JSConstructorReturnsPrimitive
        return `${contractId}`;
    }
};

export const NotificationMessageMethods = {
    /* Send notification to all users of the system including yourself */
    sendAll: 'sendAll',
    /* Send notification to all users of the system excluding yourself */
    sendToOthers: 'sendToOthers',
    /* Send notification to group */
    sendGroup: 'sendGroup',
    /* Joining a notification group */
    joinGroup: 'joinGroup',
    /* Leaving a notification group*/
    leaveGroup: 'leaveGroup',
};

/**
 * Alert hub that intercepts and send out event to notification service hub.
 * @param props
 * @returns {*}
 * @constructor
 */
const AlertsHub = props => {
    const {
        auth,
        user = {},
        addNotification,
        addAlert,
        notificationGroups,
        addNotificationGroup,
        removeNotificationGroup
        // notificationMessages = [],
        // notificationGroups,
        // setNotificationGroups,
    } = props;

    const notificationHubConnectionString = process.env.REACT_APP_NOTIFICATION_HUB || '';

    if (!notificationHubConnectionString) {
        console.error('Failed to find REACT_APP_NOTIFICATION_HUB connection string in env');
    }

    // TODO : find a better way to handle the lifecycle of the hubConnection. i.e we don't want it to be constantly disconnecting and reconnecting.
    const hubConnection = useSignalR(notificationHubConnectionString, auth.token);

    /*
     * Keeps a ref of the current groups that the user is apart of
     *
     * NB - the reason this a ref an not state is cause if state object is used
     * inside a function a snapshot is taken an it doesn't have the true value.
     */
    const currentGroupsRef = useRef({});
    // const [currentGroups, setCurrentGroups] = useState({});

    //Create listeners and start connection.
    if (hubConnection && hubConnection.state === HubConnectionState.Disconnected) {
        // Set up SignalR Event Listeners
        hubConnection.on('OnUserConnected', payload => {
            console.log('OnUserConnected', payload);
        });

        hubConnection.on('customerCreated', payload => {
            console.log('On customer created: ', payload, payload.customerEmail);
        });

        hubConnection.on('sendToAll', payload => {
            console.log('Send to All triggers ', payload);
        });

        hubConnection.on('OnNotificationReceived', notificationMessage => {
            console.log('on notification received', notificationMessage);

            const {message, type, key, payload = {}} = notificationMessage;

            const {meta} = payload;

            // TODO: consider refactoring into separate component.
            // stop if user not in group;
            if (!currentGroupsRef.current.includes(payload.groupName)) {
                console.log('Not in groups', payload.groupName, currentGroupsRef);
                return;
            }

            // display notification
            addNotification(message, type, key,);

            // add alert
            addAlert({
                id: new Date().getTime() + Math.random(),
                heading: 'Alert',
                type: 'Test',
                body: notificationMessage.message,
                seen: false,
                selected: false,
                timeCreated: new Date().getTime()
            });

            if (meta && meta.action) {
                props.dispatch(meta.action);
            }
        });

        // hubConnection.on('testNotification', (notificationMessage) => {
        //
        //     // display notification
        //     addNotification(
        //         notificationMessage.message,
        //         notificationMessage.type,
        //         notificationMessage.key,
        //     );
        //
        //     // add alerts
        //     addAlert({
        //         id: "1",
        //         heading: "Test",
        //         type: "Test",
        //         body: notificationMessage.message,
        //         seen: false,
        //         selected: false,
        //         timeCreated: new Date().getTime()
        //     })
        // });

        // start connection
        hubConnection.start()
            .then(() => {
                console.log('Connected to customers hub');
                hubConnection
                    .invoke('SendToAllClients', 'customerCreated', { customerEmail: 'somethingthatshouldntmatter@email.com' })
                    .catch(err => console.error('invoke err', err));

                // hubConnection.invoke('SendToAllClients', 'testNotification',
                // ).catch(err => console.error("invoke err", err));

                // Join Group
                const join = groupName => (hubConnection, joinMethod) => {
                    if (currentGroupsRef.current.includes(groupName)) return;
                    joinMethod(hubConnection, groupName);
                    // TODO handle the event were joining the group failed.
                    console.log('Joining group', groupName);
                    // setCurrentGroups(updatedGroups);
                    addNotificationGroup(groupName);
                };

                const joinGrace = join(NotificationGroups.Grace);
                const joinSms = join(NotificationGroups.Sms);
                const joinAdmin = join(NotificationGroups.Admin);
                const joinBAM = join(NotificationGroups.BusinessAccountManagers(user.companyId));
                const joinCustomerRep = join(NotificationGroups.CustomerRepresentatives(user.customerId));

                /**
                 * Join Groups
                 * The groups that a user joins in determined by their role.
                 * Other groups can be joined on a per use basis.
                 * TODO consider abstracting this to separate component.
                 */
                switch (auth.role) {
                case roles.GK: {
                    joinGrace(hubConnection, joinGroup);
                    break;
                }
                case roles.SMS: {
                    joinSms(hubConnection, joinGroup);
                    break;
                }
                case roles.BAM: {
                    joinBAM(hubConnection, joinGroup);
                    break;
                }
                case roles.ADMIN: {
                    joinAdmin(hubConnection, joinGroup);
                    break;
                }
                case roles.CUSTOMER_REP: {
                    joinCustomerRep(hubConnection, joinGroup);
                    break;
                }
                default: {
                    // joinGroup(hubConnection, NotificationGroups.Grace);
                    break;
                }
                }
            })
            .catch(error => console.error('Error while trying to establish connection to hub: ', error));
    }

    /*
     * Event Emitters
     */
    useEffect(() => {
        // Don't subscribe event handler unless we are in a connected state.
        if (!hubConnection /*|| hubConnection.state !== HubConnectionState.Connected*/) {
            return;
        }

        console.log('Adding event listeners');

        EventEmitter.subscribe(EVENTS.JOIN_GROUP, event => {
            joinGroup(hubConnection, event.groupName);
        });

        EventEmitter.subscribe(EVENTS.LEAVE_GROUP, event => {
            joinGroup(hubConnection, event.groupName);
        });

        EventEmitter.subscribe(EVENTS.SEND_MESSAGE_TO_GROUP, event => {
            const notificationMessage = {
                type: event.type,
                message: event.message,
                key: event.key,
                payload: event.payload
            };
            sendToGroup(hubConnection, event.payload.groupName, notificationMessage);
        });

        EventEmitter.subscribe(EVENTS.SEND_MESSAGE_TO_ALL, event => {
            sendToAll(hubConnection, 'sendToAll', event);
        });

        EventEmitter.subscribe(EVENTS.SEND_MESSAGE_TO_ALL_EXCEPT, event => {
            sendToAll(hubConnection, 'sendToAll', event);
        });

        return () => {
            EventEmitter.unsubscribe(EVENTS.JOIN_GROUP);
            EventEmitter.unsubscribe(EVENTS.LEAVE_GROUP);
            EventEmitter.unsubscribe(EVENTS.SEND_MESSAGE_TO_GROUP);
            EventEmitter.unsubscribe(EVENTS.SEND_MESSAGE_TO_ALL);
            EventEmitter.unsubscribe(EVENTS.SEND_MESSAGE_TO_ALL_EXCEPT);
        };
    }, [hubConnection, user]);

    useEffect(() => {
        const shouldLeaveGroups = !notificationGroups.length && currentGroupsRef.current.length > 0;
        if (shouldLeaveGroups) {
            for (const group of currentGroupsRef.current) {
                leaveGroup(hubConnection, group);
            }
        }

        currentGroupsRef.current = notificationGroups;
    }, [notificationGroups]);

    return null;
};

AlertsHub.propTypes = {
    auth: PropTypes.object,
    notificationMessages: PropTypes.array,
    user: PropTypes.object
};
AlertsHub.defaultProps = {
    auth: {},
    notificationMessages: [],
    user: {}
};

// Notification Service Methods
const sendToGroup = (connection, groupName, notifMessage) => {
    connection.invoke('send', groupName, notifMessage)
        .catch(err => console.error('send to group err', err));
};

const sendToAll = (connection, eventName, payload) => {
    connection.invoke('SendToAllClients', eventName, payload)
        .catch(err => console.error('send to all err', err));
};

const sendToAllExceptMe = (connection, eventName, payload) => {
    connection.invoke('SendToAllClientsExceptMe', eventName, payload)
        .catch(err => console.error('send to all except me err', err));
};

const joinGroup = (connection, groupName) => {
    connection.invoke('joinGroup', groupName)
        .catch(err => console.error('join group err', err));
};

const leaveGroup = (connection, groupName) => {
    connection.invoke('leaveGroup', groupName)
        .catch(err => console.error('leave group err', err));
};

const mapStateToProps = state => ({
    auth: state.auth,
    user: state.user,
    notificationGroups: state.notificationGroups
});

const mapDispatchToProp = dispatch => ({
    ...bindActionCreators({
        addAlert,
        addNotification,
        addNotificationGroup,
        removeNotificationGroup
    }, dispatch),
    dispatch
});

export default connect(mapStateToProps, mapDispatchToProp)(AlertsHub);
